import { useEffect, useState } from "react";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import {
  errorMessage,
  getClaimBadges,
  getRuleJobs,
  getRulesSummary,
  isAbortError,
} from "../../services/apiClient.js";
import JobList, { DASHBOARD_COLUMNS, DASHBOARD_NOTE_COLUMNS, WFM_COLUMNS, WFM_ATTENTION_FLOOR } from "../claims/JobList.jsx";

// Every fetch below is tied to an AbortController that fires on unmount and on
// any dependency change. Two reasons, both seen in the wild (Karen, rural link,
// 2026-08-04): a request nobody is waiting for any more should stop holding a
// connection while she clicks on, and a slow reply that lands AFTER a newer one
// must not overwrite what is on screen. An aborted request rejects like a
// failure, so isAbortError guards every catch.
function LoadError({ message, onRetry }) {
  return (
    <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
      <span>{message}</span>
      <Button size="sm" variant="outline-danger" onClick={onRetry}>
        Try again
      </Button>
    </Alert>
  );
}

// "Show all summary" (Karen 2026-07-19) — 1.0's RDR per-person grid rebuilt on
// the 2.0 rules engine: one mini-table per rule, columns = only the people
// with records in THAT rule (names vary section to section, like 1.0), first
// row = each person's total as a link (click → that person's jobs inline),
// sub-rows = the rule's declared split (hold reason / job type — the
// summaryBreakdown on the rule in services/workflowReport.js). Rendered in
// the bordered-grid style of Karen's second screenshot, over screenshot 1's
// data model (the meeting-doc appendix is the spec).

// One clicked cell's jobs: a person (or "none" = the Unassigned column) within
// a rule, optionally narrowed to one breakdown bucket (1.0's drill-down took
// person AND reason — Robert 2026-07-22).
function PersonJobs({ group, ruleKey, personId, bucket, params, onLoaded, noteColumn }) {
  const [jobs, setJobs] = useState(null);
  const [badges, setBadges] = useState(null);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setJobs(null);
    setError("");
    const pmParams = personId === "none" ? { pm: "none", pm_user: "" } : { pm: "other", pm_user: personId };
    getRuleJobs(group, ruleKey, { ...params, ...pmParams, bucket: bucket || "" }, { signal: controller.signal })
      .then((data) => {
        setJobs(data);
        onLoaded?.(data);
        // Badges hang off the same controller, so closing the drill-down stops
        // this one too rather than leaving it to finish for nobody.
        return getClaimBadges(data.rows.map((r) => r.id), { signal: controller.signal })
          .then(setBadges)
          .catch(() => {}); // badges are decoration — a failure here is not worth a message
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(errorMessage(err, "Unable to load the jobs."));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, ruleKey, personId, bucket, reloadToken]);

  if (error) {
    return (
      <div className="my-2">
        <LoadError message={error} onRetry={() => setReloadToken((n) => n + 1)} />
      </div>
    );
  }
  if (!jobs) return <div className="py-2"><Spinner size="sm" /></div>;
  return (
    <div className="mb-3">
      {/* Same as the detail lists (Robert 2026-07-25): each section shows only
          its own category's badges, extended — the PCO Dashboard the green
          "main" badges; the Dashboard's rows open the Progress tab on click. */}
      <JobList
        rows={jobs.rows}
        badges={badges}
        badgesExtended
        badgeGroup={group}
        rowTab={group === "main" ? "progress" : undefined}
        aligned /* drill-downs stack down the page too — same treatment */
        columns={group === "workflow" ? WFM_COLUMNS : group === "main" && noteColumn ? DASHBOARD_NOTE_COLUMNS : DASHBOARD_COLUMNS} /* a WFM drill-down is still WFM */
        attentionFloor={group === "workflow" ? WFM_ATTENTION_FLOOR : group === "main" && noteColumn ? 0 : undefined}
      />
      {jobs.rows.length >= jobs.cap ? (
        <div className="small text-warning mt-1">Showing the first {jobs.cap} — narrow this down.</div>
      ) : null}
    </div>
  );
}

// Fixed column widths so every rule's grid lines up down the page (Robert
// 2026-07-22): the title column fits the longest rule name ("In Progress With
// No Daily Review"), person columns share one width, and person columns are
// alphabetical — so the same person lands in the same place wherever possible.
const TITLE_COL = 250;
const PERSON_COL = 150;

// Whole dollars, as the Detail lines print them.
const money = (v) =>
  v == null ? null : Number(v).toLocaleString("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

function RuleGrid({ rule, group, params, openCell, onOpenCellChange, onJobsLoaded, noteColumn }) {
  // Zero dollars is noise, not information (Robert 2026-08-04): Disputed
  // Invoices was printing "Totals $0" over a lone "$0" for a job that has no
  // money on it. Blank the individual cell, and drop the whole row only when
  // there is no money ANYWHERE — a grand total can legitimately be zero while
  // real amounts cancel out, which WIP does (it runs negative per person).
  const hasMoney =
    rule.total != null &&
    (Number(rule.total) !== 0 || rule.columns.some((c) => Number(c.money) !== 0));
  // The opened cell: { person: id|"none", bucket: label|null }, or null.
  // EVERY count is a click target (Robert 2026-07-22) — totals, breakdown
  // cells, and the Unassigned column alike; clicking again closes.
  const toggleCell = (person, bucket = null) =>
    onOpenCellChange(
      openCell && openCell.person === person && openCell.bucket === bucket
        ? null
        : { person, bucket }
    );
  // On paper the count is plain text: the print stylesheet hides every button
  // (reports print clean), which took the numbers with it and printed the
  // whole summary blank (Karen Renall 2026-09-10, wfm page.pdf).
  const countButton = (n, person, bucket, extraClass = "") =>
    n ? (
      <>
        <button
          type="button"
          className={`btn btn-link btn-sm p-0 d-print-none ${extraClass}`}
          onClick={() => toggleCell(person, bucket)}
        >
          {n}
        </button>
        <span className={`d-none d-print-inline ${extraClass}`}>{n}</span>
      </>
    ) : (
      <span className={`text-muted ${extraClass}`}>0</span>
    );

  return (
    <div className="mb-3">
      <div className="table-responsive">
        <Table bordered size="sm" className="mb-1 align-middle" style={{ width: "auto" }}>
          <thead className="table-surface">
            <tr>
              {/* The rule name IS the corner cell — no separate title line. */}
              <th style={{ width: TITLE_COL, minWidth: TITLE_COL }}>{rule.label}</th>
              {rule.columns.map((c) => (
                <th
                  key={c.person_id ?? "none"}
                  className="text-center text-uppercase"
                  style={{ width: PERSON_COL, minWidth: PERSON_COL }}
                >
                  {c.person_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-muted">Total</td>
              {rule.columns.map((c) => (
                <td key={c.person_id ?? "none"} className="text-center">
                  {countButton(c.total, c.person_id ?? "none", null, "fw-semibold")}
                </td>
              ))}
            </tr>
            {(rule.breakdown?.rows || []).map((row) => (
              <tr key={row.label}>
                <td className="small text-muted">{row.label}</td>
                {rule.columns.map((c) => {
                  const n = row.cells[c.person_id ?? "none"] || 0;
                  return (
                    <td key={c.person_id ?? "none"} className="text-center small">
                      {countButton(n, c.person_id ?? "none", row.label, "small")}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Money rules carry a figure PER PERSON, not just the overall one
                the Detail line shows (Karen 2026-08-04). 1.0 put the grand
                total in the far-left cell — "Totals $1,234,567" — and each
                person's share in their own column, so that is what this is.
                Not a click target: the count row directly above already opens
                exactly the same jobs, and in 1.0 this row was flagged
                ExcludeTotalRowCount, i.e. a readout rather than a drill-down. */}
            {hasMoney ? (
              <tr>
                <td className="small fw-semibold">Totals {money(rule.total)}</td>
                {rule.columns.map((c) => (
                  // Centred to sit under the counts above rather than drifting
                  // to the cell edge; tabular figures keep the digits aligned.
                  <td key={c.person_id ?? "none"} className="text-center small fw-semibold jl-num">
                    {Number(c.money) ? money(c.money) : null}
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>
      {openCell ? (
        <PersonJobs
          group={group}
          ruleKey={rule.key}
          personId={openCell.person}
          bucket={openCell.bucket}
          params={params}
          noteColumn={noteColumn}
          onLoaded={(data) => {
            onJobsLoaded?.(data);
            // A remembered drill-down whose count has since gone to zero (the
            // filters moved on) would sit there as an empty "No claims found"
            // table — and print that way. It closes itself.
            if (data && data.rows && data.rows.length === 0) onOpenCellChange(null);
          }}
        />
      ) : null}
    </div>
  );
}

function RulesSummary({
  group,
  params,
  paramsKey,
  withRecordsOnly,
  openCells = {},
  onOpenCellChange,
  onReady,
  noteColumn = false,
}) {
  const [rules, setRules] = useState(null);
  const [loadedDrilldowns, setLoadedDrilldowns] = useState(() => new Set());
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setRules(null);
    setLoadedDrilldowns(new Set());
    setError("");
    getRulesSummary(group, params, { signal: controller.signal })
      .then(setRules)
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(errorMessage(err, "Unable to load the summary."));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, paramsKey, reloadToken]);

  const shown = rules ? (withRecordsOnly ? rules.filter((r) => r.count > 0) : rules) : [];
  const expectedDrilldowns = shown
    .filter((r) => openCells[r.key])
    .map((r) => r.key);
  const expectedKey = expectedDrilldowns.join(":");

  useEffect(() => {
    if (!rules) return;
    if (expectedDrilldowns.every((key) => loadedDrilldowns.has(key))) onReady?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, expectedKey, loadedDrilldowns]);

  if (error) return <LoadError message={error} onRetry={() => setReloadToken((n) => n + 1)} />;
  if (!rules) return <p className="text-muted pt-2 mb-0">Loading summary…</p>;

  if (shown.length === 0) {
    return <p className="text-muted pt-2 mb-0">Nothing needs attention — every report is clear.</p>;
  }
  return (
    <div className="pt-3">
      {shown.map((r) => (
        <RuleGrid
          key={`${paramsKey}:${r.key}`}
          rule={r}
          group={group}
          params={params}
          noteColumn={noteColumn}
          openCell={openCells[r.key] || null}
          onOpenCellChange={(cell) => onOpenCellChange?.(r.key, cell)}
          onJobsLoaded={() =>
            setLoadedDrilldowns((prev) => {
              if (prev.has(r.key)) return prev;
              const next = new Set(prev);
              next.add(r.key);
              return next;
            })
          }
        />
      ))}
    </div>
  );
}

export default RulesSummary;
